import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { BuildingDTO } from "shared/types/building";
import { FileLabel, FileLogStatus } from "shared/types/file";
import { UnitDTO } from "shared/types/unit";

// Calculate the date for 3 months from now
const threeMonthsFromNow = new Date();
threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

const nineMonthsBefore = new Date();
nineMonthsBefore.setMonth(nineMonthsBefore.getMonth() - 9);

const twelveMonthsBefore = new Date();
twelveMonthsBefore.setMonth(twelveMonthsBefore.getMonth() - 12);

export const calculateEnergyPerformanceDotValues = async (
    entity: any,
    session: any
) => {
    let dotValues: any = {
        red: { count: 0, labels: [] as string[] },
        orange: { count: 0, labels: [] as string[] },
        green: { count: 0, labels: [] as string[] },
        transparent: { count: 0, labels: [] as string[] },
    };

    try {
        const queryParams = new URLSearchParams();

        // Decide scope: unit vs building, mirroring electricity check style
        const isProbablyUnit = Boolean(
            (entity as UnitDTO)?.building ||
            (entity as UnitDTO)?.boxNumber ||
            !(entity as BuildingDTO)?.googlePlaceLongAddress
        );
        const isProbablyBuilding = Boolean(
            (entity as BuildingDTO)?.googlePlaceLongAddress || 
            ((entity as BuildingDTO)?.units && (entity?.units.length > 0))
        );

        if (isProbablyUnit) {
            const unitId = entity?.id || entity?.unitId || entity?.energyPerformanceInfo?.unitId;
            if (unitId) {
                queryParams.append("unitId", unitId);
            }
        } else if (isProbablyBuilding) {
            const buildingId = entity?.id || entity?.buildingId || entity?.energyPerformanceInfo?.buildingId;
            if (buildingId) {
                queryParams.append("buildingId", buildingId);
            }
        } else {
            // Fallback to explicit energyPerformanceInfo linkage when present
            const unitId = entity?.energyPerformanceInfo?.unitId;
            const buildingId = entity?.energyPerformanceInfo?.buildingId;
            if (unitId) queryParams.append("unitId", unitId);
            else if (buildingId) queryParams.append("buildingId", buildingId);
        }

        // Determine labels to request
        const labels: string[] = [];
        if (queryParams.get("unitId")) {
            labels.push(FileLabel.EPCUnit);
        }
        if (queryParams.get("buildingId")) {
            labels.push(FileLabel.EPCCommon);
        }

        if (labels.length === 0) {
            dotValues.transparent.count++;
            dotValues.transparent.labels.push("Geen EPC context (unit/gebouw) gevonden");
            return dotValues;
        }

        queryParams.append("labels", labels.join(","));

        const response = await fetchWithAuth(
            `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
            { method: "GET" },
            session
        );
        const fileLogs = await response.json();
        const currentDate = new Date();

        if (!fileLogs || fileLogs.length === 0) {
            dotValues.transparent.count++;
            dotValues.transparent.labels.push("Geen EPC aanwezig");
            return dotValues;
        }

        // Consider latest EPC-related logs per scope
        const epcLogs = fileLogs
            .filter((log: any) =>
                (Array.isArray(log.label) ? log.label : [log.label]).some((lbl: string) =>
                    lbl?.includes(FileLabel.EPCUnit) || lbl?.includes(FileLabel.EPCCommon)
                )
            )
            .sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());

        // Find most recent valid EPC
        const validEpcLog = epcLogs.find((log: any) => {
            const expirationDate = log.expirationDate ? new Date(log.expirationDate) : null;
            return (
                (log.status === FileLogStatus.OK || log.status === FileLogStatus.OK_WITH_REMARKS) &&
                (!expirationDate || expirationDate > currentDate)
            );
        });

        if (!epcLogs.length) {
            dotValues.transparent.count++;
            dotValues.transparent.labels.push("Geen EPC aanwezig");
        } else if (!validEpcLog) {
            dotValues.red.count++;
            dotValues.red.labels.push("Geen geldig EPC aanwezig");
        } else {
            // Base presence status
            if (validEpcLog.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("EPC aanwezig");
            } else {
                dotValues.orange.count++;
                dotValues.orange.labels.push("EPC aanwezig met opmerkingen");
            }

            // Expiration within 3 months
            const expirationDate = validEpcLog.expirationDate ? new Date(validEpcLog.expirationDate) : null;
            if (expirationDate && expirationDate <= threeMonthsFromNow) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("EPC verloopt binnen drie maanden");
            }
        }
    } catch (error) {
        console.error("Error fetching EPC file logs:", error);
    }

    return dotValues;
};
